import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import startCase from 'lodash/startCase';
import styles from './BaseDetails.module.scss';
import DetailGeneralInfo from 'components/DetailGeneralInfo';
import PanelHeaderBar from 'components/PanelHeaderBar';
import Section from 'components/Section';
import cn from 'classnames';
import valueType from 'components/Details';
import Portal from 'portal/Portal';
import CreateShiftActivityButton from 'containers/Pages/People/Attendance/Layouts/addShiftActivityButton';
import Button from 'components/Button';
import { useTranslation } from 'react-i18next';
import { Box, makeStyles } from '@material-ui/core';
import Loader from 'components/Loader';
import { MASK } from 'utils/constants/css';

const useStyles = makeStyles(theme => ({
  right: {
    position: 'absolute',
    zIndex: 5,
    height: '75vh',
    width: '35rem',
    right: 0,
    display: 'flex',
    color: theme.palette.primary.main,
    flexDirection: 'column',
    background: theme.palette.background.default,
    paddingBottom: '2rem',
    border: `1px solid #58595b`,
    '& ::-webkit-scrollbar': {
      width: '0.7em',
      height: '0.7em',
    },
    '& ::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
    },
    '& ::-webkit-scrollbar-thumb': {
      borderRadius: '10px',
      backgroundColor: theme.palette.primary.main,
    },
    '& ::-webkit-scrollbar-corner': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  mask: {
    position: 'absolute',
    opacity: MASK.OPACITY_LOW,
    left: 0,
    height: '100%',
    width: 'inherit',
    background: theme.palette.text.disabled,
    zIndex: 1000,
  },
  detailContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  keyColor: {
    color: theme.palette.text.secondary,
    flex: '1 1 35%',
    display: 'flex',
    minWidth: '6rem',
    paddingRight: '0.3rem',
    boxSizing: 'border-box',
  },
}));

const BaseDetails = ({
  header,
  details,
  cancel,
  editable,
  onEdit,
  operationComponent,
  className,
  showConfirmPopup,
  closeConfirmPopup,
  FormComponent,
  downloadable,
  onDownloadClick,
  editHoverText,
  downloadHoverText,
  createShiftButtonComponent,
  goToParentButton,
  onParentButtonClick,
  loading,
  FollowupButtonComponent,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Box className={cn(classes.right, className)}>
      {loading && (
        <div className={classes.mask}>
          <Loader outline={true} />
        </div>
      )}
      <PanelHeaderBar
        closable={cancel !== undefined}
        onClose={cancel}
        title={t(header)}
        onEdit={onEdit}
        editable={editable}
        downloadable={downloadable}
        onDownload={onDownloadClick}
        editHoverText={t(editHoverText)}
        downloadHoverText={t(downloadHoverText)}
      />
      <div className={styles.mainFrame}>
        {details?.noSectionFields && (
          <DetailGeneralInfo data={details.noSectionFields} />
        )}
        {details?.auditTrailSection && (
          <Section text="Audit Trail">
            <div className={styles.detailInfo}>
              {Object.entries(details.auditTrailSection).map((item, k) => {
                const [key, value] = item;
                return (
                  <Fragment key={k}>
                    <div className={styles.detailInfoLabel}>
                      {t(value.label) || t(startCase(key))}
                    </div>
                    {Array.isArray(value)
                      ? value.map((item, k) => (
                          <div className={styles.detailInfoText} key={k}>
                            {get(item, 'display', '-')}
                          </div>
                        ))
                      : valueType(value)}
                  </Fragment>
                );
              })}
            </div>
          </Section>
        )}
        {details?.overviewSection && (
          <Section text="Overview">
            <DetailGeneralInfo data={details.overviewSection} />
          </Section>
        )}

        {details?.locationSection && (
          <Section text="Location">
            <div className={styles.detailInfo}>
              {Object.entries(details.locationSection).map((item, k) => {
                const [key, value] = item;
                return (
                  <Fragment key={k}>
                    <div className={styles.detailInfoLabel}>
                      {t(value.label) || t(startCase(key))}
                    </div>
                    {Array.isArray(value)
                      ? value.map((item, k) => (
                          <div className={styles.detailInfoText} key={k}>
                            {get(item, 'display', '-')}
                          </div>
                        ))
                      : valueType(value)}
                  </Fragment>
                );
              })}
            </div>
          </Section>
        )}
        {details?.descriptionSection && (
          <Section text="Descriptions">
            <DetailGeneralInfo data={details.descriptionSection} />
          </Section>
        )}
        {details?.generalInfo && (
          <Section text="General Information">
            {details.generalInfo && (
              <DetailGeneralInfo
                data={details?.generalInfo}
                imageSrc={details?.others && get(details, 'others.image')}
                status={get(details, 'others.availabilityStatus')}
              />
            )}
          </Section>
        )}
        {(details?.detailedInfo || details?.multiDetailGeneralInfo) && (
          <>
            <Section text="Detailed Information">
              <div className={styles.detailInfo}>
                {details.detailedGeneralInfo && (
                  <DetailGeneralInfo
                    data={details.detailedGeneralInfo}
                    imageSrc={get(details, 'others.detailedInfoImage')}
                    className={styles.detailedGeneralInfo}
                  />
                )}
                {details.multiDetailGeneralInfo &&
                  details.multiDetailGeneralInfo.map(
                    ({ photoUrl, ...item }, index) => {
                      return (
                        <DetailGeneralInfo
                          key={index}
                          data={item}
                          imageSrc={photoUrl}
                          className={cn(
                            styles.multiInfo,
                            styles.detailedGeneralInfo,
                          )}
                        />
                      );
                    },
                  )}
                {details?.detailedInfo &&
                  Object.entries(details.detailedInfo).map((item, k) => {
                    const [key, value] = item;
                    /**
                     * TODO: the CreateShiftActivityButton should be an AddSubTableItem and should take a label and a function
                     */

                    return (
                      <div
                        className={cn({
                          // these type should not be flex rows
                          [classes.detailContainer]:
                            value?.type !== 'list-data' &&
                            value?.type !== 'map-array' &&
                            value?.type !== 'detailImageTable' &&
                            value?.type !== 'map-marker' &&
                            value?.type !== 'image-marker' &&
                            value?.type !== 'map-compound' &&
                            value?.type !== 'media' &&
                            value?.type !== 'trip-locations',
                        })}
                        key={k}
                      >
                        <div
                          className={cn(
                            styles.detailInfoLabel,
                            classes.keyColor,
                          )}
                        >
                          {t(value.label) || t(startCase(key))}
                        </div>
                        {createShiftButtonComponent && !value?.noButton && (
                          <CreateShiftActivityButton />
                        )}
                        {Array.isArray(value)
                          ? value.map((item, k) => (
                              <div className={styles.detailInfoText} key={k}>
                                {get(item, 'display', '-')}
                              </div>
                            ))
                          : valueType(value)}
                      </div>
                    );
                  })}
              </div>
            </Section>
          </>
        )}
        {details?.incidentReviewSection && (
          <Section text="Incident Review">
            <div className={styles.detailInfo}>
              {Object.entries(details.incidentReviewSection).map((item, k) => {
                const [key, value] = item;
                return (
                  <Fragment key={k}>
                    <div className={styles.detailInfoLabel}>
                      {t(value.label) || t(startCase(key))}
                      {value.enableFollowupButton && (
                        <FollowupButtonComponent
                          view={value.view}
                          titleOfEvent={t(details.noSectionFields.title.value)}
                          followupEventID={details.others.id.value}
                          parentEventDetails={details}
                        />
                      )}
                    </div>

                    {Array.isArray(value)
                      ? value.map((item, k) => (
                          <div className={styles.detailInfoText} key={k}>
                            {get(item, 'display', '-')}
                          </div>
                        ))
                      : valueType(value)}
                  </Fragment>
                );
              })}
            </div>
          </Section>
        )}
        {details?.originalReportOverviewSection && (
          <Section text="Original Report Overview">
            <DetailGeneralInfo data={details.originalReportOverviewSection} />
          </Section>
        )}
        {goToParentButton && (
          <Button
            onClick={onParentButtonClick}
            text={'Go to Parent Event'}
            size="default"
            color="primary"
            className={styles.parentButton}
          />
        )}
        {operationComponent && (
          <Section text="Operational Information">{operationComponent}</Section>
        )}
      </div>
      {showConfirmPopup && (
        <Portal id={'portal'}>
          <FormComponent closeConfirmPopup={closeConfirmPopup} />
        </Portal>
      )}
    </Box>
  );
};

BaseDetails.propTypes = {
  reply: PropTypes.bool,
  onReply: PropTypes.func,
  editable: PropTypes.bool,
  onEdit: PropTypes.func,
  header: PropTypes.string,
  details: PropTypes.object,
  cancel: PropTypes.func.isRequired,
  operationComponent: PropTypes.element,
  followupButtonComponent: PropTypes.element,
  className: PropTypes.string,
  showConfirmPopup: PropTypes.bool,
  closeConfirmPopup: PropTypes.func,
  FormComponent: PropTypes.elementType,
  FollowupButtonComponent: PropTypes.elementType,
  downloadable: PropTypes.bool,
  goToParentButton: PropTypes.bool,
  onDownloadClick: PropTypes.func,
  onParentButtonClick: PropTypes.func,
  editHoverText: PropTypes.string,
  downloadHoverText: PropTypes.string,
  createShiftButtonComponent: PropTypes.bool,
  loading: PropTypes.bool,
};

export default BaseDetails;
